import React, { useState, useEffect, useRef } from 'react';
import { MagnifyingGlass, X, CaretDown, User as UserIcon, SpinnerGap, CheckCircle } from '@phosphor-icons/react';
import { messagesAPI } from '../api/api';
import { Input } from './ui/input';

/**
 * SignerPicker — searchable autocomplete that lets a contractor pick a
 * platform user as a signer without copy-pasting MongoDB _ids.
 *
 * Props:
 *  - value: { user_id, name, email, role } | null
 *  - onChange(picked)
 *  - role: default role for newly picked users ('contractor' | 'worker' | 'homeowner')
 *  - excludeUserIds?: array of user_ids to hide from results
 *  - placeholder?: string
 *  - testidPrefix?: string (for data-testid namespacing)
 */
export default function SignerPicker({
  value,
  onChange,
  role = 'worker',
  excludeUserIds = [],
  placeholder = 'Search by name, email, or company…',
  testidPrefix = 'signer-picker',
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!query.trim() || query.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await messagesAPI.searchUsers(query.trim());
        const all = res.data.users || [];
        setResults(all.filter(u => !excludeUserIds.includes(u._id)));
      } catch (err) {
        console.error('User search failed:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, excludeUserIds]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handlePick = (user) => {
    onChange?.({
      user_id: user._id,
      name: user.name || '',
      email: user.email || '',
      role: user.user_type || role,
    });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onChange?.(null);
    setQuery('');
    setOpen(true);
  };

  // If we have a picked value, show the pill instead of the input
  if (value && value.user_id) {
    return (
      <div className="flex items-center justify-between bg-emerald-50 border-2 border-emerald-400 rounded-lg p-2.5" data-testid={`${testidPrefix}-selected`}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle size={16} weight="fill" className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-neutral-900 truncate">{value.name}</p>
            <p className="text-[11px] text-neutral-500 truncate">{value.email} · {value.role}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="h-7 w-7 flex items-center justify-center rounded text-neutral-500 hover:bg-neutral-100"
          data-testid={`${testidPrefix}-clear`}
          aria-label="Remove signer"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="pl-9 h-10 text-sm"
          data-testid={`${testidPrefix}-input`}
        />
        {loading && (
          <SpinnerGap size={16} weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2B4C6F] animate-spin" />
        )}
      </div>

      {open && query.trim().length >= 2 && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-black rounded-lg shadow-[3px_3px_0px_#000] z-50 max-h-72 overflow-y-auto"
          data-testid={`${testidPrefix}-dropdown`}
        >
          {loading && results.length === 0 ? (
            <div className="p-4 text-center text-sm text-neutral-500">Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-sm text-neutral-500">
              No matches for &ldquo;<span className="font-mono">{query}</span>&rdquo;
            </div>
          ) : (
            <ul>
              {results.map((u) => (
                <li key={u._id}>
                  <button
                    type="button"
                    onClick={() => handlePick(u)}
                    className="w-full px-3 py-2.5 flex items-center gap-3 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 text-left"
                    data-testid={`${testidPrefix}-result-${u._id}`}
                  >
                    <div className="w-9 h-9 bg-[#2B4C6F] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">
                        {(u.name || u.email || '?').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-neutral-900 truncate">{u.name || '(no name)'}</p>
                      <p className="text-[11px] text-neutral-500 truncate">
                        {u.email}
                        {u.company ? ` · ${u.company}` : ''}
                      </p>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-neutral-100 border border-neutral-200 rounded text-neutral-600">
                      {u.user_type || 'user'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
